import React, { useEffect, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { X, ExclamationTriangle, Plus } from 'react-bootstrap-icons';
import { closeLocalCaja } from '../../../../../../../../crud/management_api/locales_caja_management';
import { toast } from 'react-toastify';
import './CloseCaja.scss';
import { formatCurrency } from '../../../../../../../../utils/functions';


interface RenderModalProps {
  caja: any;
  cajas: any[];
  setCajas: (cajas: any[]) => void;
  show: boolean;
  setShow: (show: boolean) => void;
  onClose?: () => void;
  setActioncaja?: (closing: boolean) => void;
  hasPendientes?: boolean;
  openCajasCount?: number;
}


const CloseCaja: React.FC<RenderModalProps> = ({
  caja,
  cajas,
  setCajas,
  show,
  setShow,
  onClose,
  setActioncaja,
  hasPendientes,
  openCajasCount = 0
}) => {
  const [finalAmount, setFinalAmount] = useState<number | null>(caja.current_amount);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      setFinalAmount(caja.current_amount ?? 0);
    }
  }, [show, caja.current_amount]);

  // Calculate differences for warnings
  const expectedAmount = caja.current_amount ?? 0;
  const declaredAmount = finalAmount ?? 0;
  const difference = declaredAmount - expectedAmount;

  const handleCloseCaja = () => {
    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    const userId = user ? Number(user.user_id) : 0;

    if (finalAmount === null) {
      toast.error('Debe ingresar un monto final válido');
      return;
    }

    // Solo verificar pendientes en el frontend si hay una sola caja abierta
    // Si hay múltiples cajas, permitir el cierre y dejar que el backend valide si es necesario
    if (openCajasCount === 1 && hasPendientes) {
      toast.error('No es posible cerrar la caja: existen pedidos o recibos pendientes y es la última caja abierta.');
      return;
    }

    setIsSubmitting(true);
    closeLocalCaja(userId, caja.workstation_id, finalAmount!)
      .then((response) => {
        setCajas(cajas.map((c: any) =>
          c.workstation_id === caja.workstation_id ? { ...c, ...response.data } : c
        ));
        toast.success('Caja cerrada con éxito');
        setShow(false);
        if (setActioncaja) setActioncaja(true);
        if (onClose) onClose();
      })
      .catch((error) => {
        console.error('Error closing caja: ', error);
        if (error.response?.status === 400) {
          // El backend retorna 400 cuando hay recibos o pedidos pendientes
          const errorMessage = error.response?.data?.message || 'Error cerrando caja';
          toast.error(errorMessage);
        } else if (error.response?.status === 401) {
          toast.error('Existen transacciones pendientes de aprobar');
          toast.error(
            'Para cerrar la caja se deben aprobar los pedidos en efectivo pendientes'
          );
        } else {
          toast.error('Error cerrando caja');
        }
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <Modal show={show} onHide={() => setShow(false)} className="close-caja-modal" centered>
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">Cerrar Caja</Modal.Title>
        </div>
        <Button variant="none" onClick={() => setShow(false)}>
          <X size={24} />
        </Button>
      </Modal.Header>
      <Modal.Body>
        <div className="amount-input-section">
          {/* Current amount reference */}
          <div className="current-amount-reference">
            <span className="reference-label">Monto actual en caja:</span>
            <span className="reference-amount">{formatCurrency(expectedAmount)}</span>
          </div>
          
          <div className="form-field">
            <div className="field-label">Ingrese el monto final</div>
            <div className="amount-input-group">
              <div className="currency-symbol">$</div>
              <input
                type="text"
                className="amount-input"
                value={
                  finalAmount != null
                    ? finalAmount.toLocaleString('es-CR', {
                        maximumFractionDigits: 0,
                      })
                    : ''
                }
                onChange={(e) => {
                  const onlyNums = e.target.value.replace(/\D/g, '');
                  setFinalAmount(onlyNums ? Number(onlyNums) : null);
                }}
                placeholder="0"
              />
            </div>
          </div>
        </div>

        {/* Warning banners */}
        {finalAmount !== null && finalAmount !== expectedAmount && (
          <>
            {difference < 0 && (
              <div className="warning-banner withdrawal-warning">
                <ExclamationTriangle className="warning-icon" />
                <div className="warning-content">
                  <div className="warning-title">Retiro de dinero</div>
                  <div className="warning-message">
                    Un retiro de dinero de <span className="amount-highlight">{formatCurrency(Math.abs(difference))}</span> será registrado
                  </div>
                </div>
              </div>
            )}
            
            {difference > 0 && (
              <div className="warning-banner deposit-warning">
                <Plus className="warning-icon" />
                <div className="warning-content">
                  <div className="warning-title">Abono de dinero</div>
                  <div className="warning-message">
                    Un abono de <span className="amount-highlight">{formatCurrency(difference)}</span> será registrado
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button
            variant="none"
            className="form-button-cancel"
            onClick={() => setShow(false)}
          >
            Cancelar
          </Button>
          <Button 
            variant="none" 
            onClick={handleCloseCaja} 
            className="form-button-create" 
            disabled={(openCajasCount === 1 && hasPendientes) || isSubmitting} 
            title={(openCajasCount === 1 && hasPendientes) ? 'No se puede cerrar: hay pedidos o recibos pendientes y es la última caja abierta' : undefined}
          >
            {isSubmitting ? (
              <>
                Cerrando
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="ms-2"
                />
              </>
            ) : (
              'Cerrar'
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CloseCaja;